import React, { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import useAxios from 'axios-hooks'
import { lowerCase } from 'lodash'
import { Viewer } from '@bytemd/react'
import ReactPlayer from 'react-player/vimeo'

import { TutorialLoading } from './components/loading'
import BreadcrumbHeader from '~/components/BreadcrumbHeader'
import { MODULES_DATA } from '~/constants/tutorialModules'
import { TutorialWrapper } from './styles'
import { Codesandbox } from 'react-feather'
import gfm from '@bytemd/plugin-gfm'
import breaks from '@bytemd/plugin-breaks'
import highlight from '@bytemd/plugin-highlight'

const TutorialPage = () => {
  const history = useHistory()
  const { tutorialId } = useParams()
  const [module, setModule] = useState('Module')
  const [submodule, setSubmodule] = useState('Submodule')
  const [title, setTitle] = useState('Tutorial')
  const [image, setImage] = useState('')
  const [video, setVideo] = useState('')
  const [content, setContent] = useState('')
  const [hasAside, setHasAside] = useState(true)

  const [{ loading, error, data }] = useAxios({
    url: `/tutorial/${tutorialId}/page`,
    method: 'GET',
  })

  useEffect(() => {
    if (!error) return
    history.push('/404')
  }, [error])

  useEffect(() => {
    if (!data) return
    setModule(MODULES_DATA[data.tutorial?.module]?.name || 'Module')
    setSubmodule(
      MODULES_DATA[data?.tutorial?.module]?.submodules?.[
        data?.tutorial?.submodule
      ] || 'Submodule'
    )
    setTitle(data?.tutorial?.title)
    setHasAside(data?.relatedTutorials?.length || data?.otherModules?.length)
    setImage(data?.tutorial?.image)
    setVideo(data?.tutorial?.video)
    setContent(data?.tutorial?.content)
  }, [data])

  return (
    <div>
      {loading ? (
        <TutorialLoading />
      ) : (
        <div>
          <BreadcrumbHeader
            path={[
              { name: 'Help Center', path: '/help-center' },
              {
                name: module,
                path: `/tutorial/module/${lowerCase(module)}`,
              },
              {
                name: submodule,
                path: `/tutorial/module/${lowerCase(module)}`,
              },
              {
                name: title,
              },
            ]}
          />

          <TutorialWrapper>
            <div>
              <h2>
                <FormattedMessage id={`${lowerCase(module)} module`} />
              </h2>
              <div className="content-row">
                <div className="content-card">
                  <h1>{title}</h1>
                  {video && (
                    <div className="video-container">
                      <ReactPlayer
                        url={video}
                        controls={true}
                        width="100%"
                        height="100%"
                      />
                    </div>
                  )}
                  {image && !video && <img src={image} alt="" />}
                  <Viewer
                    className="markdown-body"
                    value={content}
                    plugins={[gfm(), breaks(), highlight()]}
                    remarkRehype={{
                      allowDangerousHtml: false,
                    }}
                  />
                </div>
                {hasAside && (
                  <div className="content-aside">
                    {Boolean(data?.relatedTutorials?.length) && (
                      <div className="related-card">
                        <h3>
                          <FormattedMessage id="Other tutorials" />
                        </h3>
                        {data?.relatedTutorials?.map(tutorial => (
                          <a
                            key={tutorial.id}
                            className="tutorial-card"
                            href={`/tutorial/${tutorial.id}`}
                          >
                            <span className="tutorial-submodule">
                              <FormattedMessage
                                id={
                                  MODULES_DATA[tutorial.module]?.submodules?.[
                                    tutorial.submodule
                                  ] || 'Unknown'
                                }
                              />
                            </span>
                            <div>{tutorial.title}</div>
                          </a>
                        ))}
                      </div>
                    )}
                    {Boolean(data?.otherModules?.length) && (
                      <div className="modules-card">
                        <h3>
                          <FormattedMessage id="Other modules" />
                        </h3>
                        {data?.otherModules?.map(module => (
                          <a
                            key={module}
                            className="module-card"
                            href={`/tutorial/module/${lowerCase(module)}`}
                          >
                            <div className="module-icon">
                              {MODULES_DATA[module]?.icon ? (
                                MODULES_DATA[module].icon
                              ) : (
                                <Codesandbox color="#fff" />
                              )}
                            </div>
                            <div>
                              <FormattedMessage
                                id={MODULES_DATA[module]?.name || 'Module'}
                              />
                            </div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TutorialWrapper>
        </div>
      )}
    </div>
  )
}

export default TutorialPage
